import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, CalendarDays, LogOut, Mail,
  ShieldCheck, Radio, AlertTriangle, ClipboardCheck, CloudSun, FlaskConical, Menu, Boxes, Package, Users, GraduationCap, ShoppingBag, Sparkles, Briefcase, Building2, Settings, StickyNote, Award,
} from "lucide-react";
import { DroneIcon } from "@/components/DroneIcon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import AiAssistant from "@/components/AiAssistant";

const nav = [
  { to: "/jobs", label: "Jobs", icon: Briefcase, id: "nav-jobs" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "nav-calendar" },
  { to: "/notes", label: "Notes", icon: StickyNote, id: "nav-notes" },
  { to: "/emails", label: "Emails", icon: Mail, id: "nav-emails" },
  { to: "/clients", label: "Clients", icon: Building2, id: "nav-clients" },
  { to: "/assets", label: "Assets", icon: Boxes, id: "nav-assets" },
  { to: "/parts", label: "Parts", icon: Package, id: "nav-parts" },
  { to: "/training", label: "Training", icon: GraduationCap, id: "nav-training" },
  { to: "/casa", label: "CASA", icon: ShieldCheck, id: "nav-casa" },
  { to: "/naips", label: "NAIPS", icon: Radio, id: "nav-naips" },
  { to: "/notam", label: "NOTAM", icon: AlertTriangle, id: "nav-notam" },
  { to: "/risk", label: "Risk", icon: ClipboardCheck, id: "nav-risk" },
  { to: "/weather", label: "Weather", icon: CloudSun, id: "nav-weather" },
  { to: "/damp", label: "DAMP", icon: FlaskConical, id: "nav-damp" },
];

const adminNav = [
  { to: "/orders", label: "Orders", icon: ShoppingBag, id: "nav-orders" },
  { to: "/users", label: "Users", icon: Users, id: "nav-users" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = user?.role === "admin" ? [...nav, ...adminNav] : nav;

  const NavLink = ({ n, onClick }) => {
    const active = location.pathname === n.to;
    return (
      <Link
        to={n.to}
        onClick={onClick}
        data-testid={n.id}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
          active ? "bg-lime-400 text-slate-900" : "text-slate-300 hover:text-white hover:bg-slate-800"
        }`}
      >
        {n.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-slate-900 text-white">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-lg bg-lime-400 flex items-center justify-center">
              <DroneIcon className="h-5 w-5 text-slate-900" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight hidden sm:block">Air Log</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 overflow-x-auto flex-1 justify-center">
            {navItems.map((n) => <NavLink key={n.to} n={n} />)}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="open-ai-btn"
              onClick={() => setAiOpen((o) => !o)}
              className="hidden lg:flex bg-lime-400 hover:bg-lime-300 text-slate-900 font-medium rounded-lg h-9"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> AI
            </Button>
            <Avatar className="h-9 w-9 border-2 border-slate-700 hidden lg:flex">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-slate-700 text-lime-400 text-sm">{user?.name?.charAt(0) || "U"}</AvatarFallback>
            </Avatar>
            <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-white transition-colors p-1.5 hidden lg:block" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>

            {/* Mobile / tablet quick nav — on the same row as the logo (logo = Dash) */}
            <nav className="lg:hidden flex items-center gap-4" data-testid="mobile-top-nav">
              {[nav[0], nav[1]].map((n) => {
                const active = location.pathname === n.to;
                return (
                  <Link key={n.to} to={n.to} data-testid={`${n.id}-mobile`} className={`flex flex-col items-center gap-0.5 text-[10px] font-medium ${active ? "text-lime-400" : "text-slate-300"}`}>
                    <n.icon className="h-5 w-5" />
                    {n.label}
                  </Link>
                );
              })}
              <button data-testid="open-ai-btn-mobile" onClick={() => setAiOpen(true)} className="flex flex-col items-center gap-0.5 text-[10px] font-medium text-lime-400">
                <span className="h-8 w-8 rounded-full bg-lime-400 flex items-center justify-center shadow-md shadow-lime-400/30">
                  <Sparkles className="h-4 w-4 text-slate-900" />
                </span>
                AI
              </button>
              <button data-testid="menu-btn" onClick={() => setMenuOpen(true)} className="flex flex-col items-center gap-0.5 text-[10px] font-medium text-slate-300">
                <Menu className="h-5 w-5" /> More
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Full menu drawer (opened from the top "More" button) */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="bg-slate-900 border-slate-800 w-72 overflow-y-auto">
          <SheetHeader><SheetTitle className="text-white font-heading text-left">Menu</SheetTitle></SheetHeader>
          <div className="mt-6 flex flex-col gap-1 pb-8">
            {navItems.map((n) => <NavLink key={n.to} n={n} onClick={() => setMenuOpen(false)} />)}
            <button onClick={logout} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-slate-800 mt-2">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>

      <AiAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}
